import { UFOv2 } from '@atlassian/post-office-frontend-performance-tracking';
import { MessageContextProvider, type MessageContextData } from '@post-office/message-context';
import { RecommendationProvider } from '@post-office/recommendation-context';
import type { MessageCreationType } from '@post-office/message-template-contracts/message-template';
import type { RecommendationSession } from '@post-office/message-template-contracts/recommendations';
import {
	type FunctionComponent,
	type ReactNode,
	Suspense,
	type SuspenseProps,
	useMemo,
} from 'react';
import { ChoreographerWrapper } from './choreographer-wrapper';
import { MaybeChoreographerWrapper } from './maybe-choreographer-wrapper';
import { FeatureFlaggedUfoWrapper } from './ufo-wrapper';
import { MaybeTriggeredMessageValidator } from './maybe-triggered-message-validator';
import { MessageAnalyticsContextProvider } from '../message-analytics-context';
import { MaybeManualMessageViewedEvent } from '@post-office/placement-contexts/maybe-manual-message-viewed-event';
import { type MessageCategory } from '../types';
import type { WaitForTriggerFunction } from '../hooks/use-wait-for-trigger';
import { type JSX } from 'react/jsx-runtime';
import { useSubscriptionConfig } from '../create-placement-component/providers/stable-graphql-subscription';

type ComponentProps = {
	transactionAccountId?: string;
};

type ProviderProps = {
	messageTemplateId: string;
	messageInstanceId: string;
	messageCategory: MessageCategory;
	transactionAccountId?: string;
	recommendationSession?: RecommendationSession;
	product?: string;
	loadingFallback?: React.ReactNode;
	postOffice?: {
		analyticsDetails?: MessageContextData['analyticsDetails'];
	};
	triggerId?: string;
	messageCreationType?: MessageCreationType;
	messageSuccessComponent?: JSX.Element;
	componentProps: ComponentProps;
	isUFOEnabled: boolean;
	children?: ReactNode;
	SuspenseBoundary?: FunctionComponent<SuspenseProps>;
	waitForTrigger?: WaitForTriggerFunction;
	configSetId?: string;
	journeyId?: string;
	journeyTriggerId?: string;
};

const DEFAULT_RECOMMENDATION_SESSION = {};

export const MessageRendererProvider = ({
	messageInstanceId,
	messageTemplateId,
	messageCategory,
	recommendationSession,
	messageSuccessComponent,
	componentProps,
	loadingFallback,
	triggerId,
	messageCreationType,
	postOffice,
	isUFOEnabled,
	children,
	SuspenseBoundary = Suspense,
	waitForTrigger,
	configSetId,
	journeyId,
	journeyTriggerId,
}: ProviderProps): JSX.Element => {
	const subscriptionConfig = useSubscriptionConfig();
	const subscriptionEnabled = subscriptionConfig.subscription?.enabled ?? false;

	/**
	 * Type guard: messageContextMemo must match MessageContextData exactly.
	 * If a field is missing or wrong type, the type annotation will catch it.
	 */
	const loadingComponent = useMemo(
		() =>
			isUFOEnabled ? (
				<UFOv2.UFOLoadHold name={UFOv2.HoldNames.MESSAGE}>
					{loadingFallback ?? null}
				</UFOv2.UFOLoadHold>
			) : (
				<>{loadingFallback ?? null}</>
			),
		[loadingFallback, isUFOEnabled],
	);

	const messageContextMemo = useMemo<MessageContextData>(
		() => ({
			messageInstanceId,
			messageTemplateId,
			triggerId,
			messageCreationType,
			transactionAccountId: componentProps.transactionAccountId,
			analyticsDetails: {
				...postOffice?.analyticsDetails,
				subscriptionEnabled,
			},
			journeyId,
			journeyTriggerId,
			configSetId,
		}),
		[
			messageInstanceId,
			messageTemplateId,
			triggerId,
			messageCreationType,
			componentProps.transactionAccountId,
			postOffice?.analyticsDetails,
			subscriptionEnabled,
			journeyId,
			journeyTriggerId,
			configSetId,
		],
	);

	// MaybeChoreographerWrapper sits at the top so it can claim its slot while the
	// lazy template is loading; it no-ops for client-triggered messages, which handle
	// their own choreography via MaybeTriggeredMessageValidator below.
	return (
		<MaybeChoreographerWrapper
			ChoreographerWrapper={ChoreographerWrapper}
			enabled={!waitForTrigger}
			messageId={messageInstanceId}
			messageTemplateId={messageTemplateId}
			messageCategory={messageCategory}
		>
			<FeatureFlaggedUfoWrapper messageTemplateId={messageTemplateId} isEnabled={isUFOEnabled}>
				<MessageContextProvider value={messageContextMemo}>
					<RecommendationProvider value={recommendationSession ?? DEFAULT_RECOMMENDATION_SESSION}>
						<SuspenseBoundary fallback={loadingComponent}>
							<MaybeTriggeredMessageValidator waitForTrigger={waitForTrigger}>
								<MessageAnalyticsContextProvider>
									<MaybeManualMessageViewedEvent isManual={!!waitForTrigger}>
										{!waitForTrigger && messageSuccessComponent}
										{children}
									</MaybeManualMessageViewedEvent>
								</MessageAnalyticsContextProvider>
							</MaybeTriggeredMessageValidator>
						</SuspenseBoundary>
					</RecommendationProvider>
				</MessageContextProvider>
			</FeatureFlaggedUfoWrapper>
		</MaybeChoreographerWrapper>
	);
};
